import { database } from '@app/firebase';
import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import ButtonLink from '@components/button-link/ButtonLink';
import HomesCarousel from '@components/carousels/HomesCarousel';
import HowItWorksMovingContainer from '@components/staging/HowItWorksMoving';
import MovingIncludes from '@components/staging/MovingIncludes';
import Testimonials from '@components/testimonials/Testimonials';
import { GENERAL_PHONE_NUMBER } from '@data/src/phoneNumbers';
import { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { makeSeoData } from 'config/next-seo.config';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import { rgba } from 'polished';
import React from 'react';

import useStyles from './Moving.style';

const title = 'Local Moving Services';
const description = 'Your #1 partner for moving, packing, decluttering, organizing and junk removal.';
const image = '/images/moving/header-image.png';

export const getServerSideProps: GetServerSideProps = async () => {
  const homes = await getDocs(query(collection(database, 'homes')
    ,where('published', '==', true)
    ,where('featured', '==', true)
    ,orderBy('install_date', 'desc')
    ,limit(10)));

  const data = {
    homes: JSON.stringify(homes.docs.map(h => h.data())),
  };

  return { props: { data } };
};

function Moving(props) {
  const { classes, theme } = useStyles();
  const { lg, md } = useBreakpoints();
  const homes = JSON.parse(props.data.homes) as guesthouse.Home[];

  return (
    <>
      <AppBackgroundColor
        backgroundColor="#F7F7F7"
        borderColor="#F7F7F7"
        headerColor="#F7F7F7"
      />

      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description, image })} />

      <ContentBlock
        maxWidth={'none'}
        padding={[0, 0]}
        margin={[0, 0]}
        style={{ backgroundColor: '#DDDDDD' }}
      >
        <ContentBlock
          maxWidth={1365}
          margin={[0, 4]}
          paddingMd={[12, 4]}
          style={{ backgroundColor: '#DDDDDD', margin: 'auto' }}
        >
          <Grid
            container
            justifyContent="space-between"
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <>
                <div style={{ marginTop: md ? theme.spacing(0) : theme.spacing(7.5), paddingLeft: theme.spacing(5), alignItems: 'center' }}>
                  <div
                    className={classes.mainImage}
                  >
                    <div style={{ zIndex: 2, position: 'relative', left: lg ? -40 : md ? -50 : -90, top: lg ? -40 : md ? 40 : 0 }}>
                      <Image
                        src="/images/moving/header-image.png"
                        alt=""
                        width={lg ? 360 : md ? 278 : 195}
                        height={lg ? 464 : md ? 358 : 252}
                        style={{
                          maxWidth: '100%',
                          height: 'auto'
                        }}
                      />
                    </div>
                    <span
                      className={classes.circle}
                      style={{ backgroundColor: '#CB8E83' }}
                    />
                  </div>
                </div>
              </>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              style={{ marginTop: md ? theme.spacing(0) : theme.spacing(-16) }}
            >
              <Typography
                component="h1"
                variant="h1"
                style={{ marginBottom: theme.spacing(3), lineHeight: md ? 1.15 : 1.3 }}
              >
                Everything you need for your next move
              </Typography>
              <Typography
                style={{ marginBottom: theme.spacing(7.5), color: '#666', lineHeight: md ? 1.5 : 1.42 }}
              >
                Whether you&apos;re moving out, throwing stuff away or just need a hand unboxing, Guest House is here to help.
              </Typography>
              <Grid
                container
                justifyContent="space-between"
              >
                <Grid
                  item
                  md={6}
                  xs={12}
                >
                  <Button
                    size="large"
                    variant="contained"
                    color="primary"
                    component={ButtonLink}
                    href="/home-staging/get-a-quote?step=email"
                    style={{ width: md ? 250 : '100%' }}
                  >
                    Get an instant quote
                  </Button>
                </Grid>
                <Grid
                  item
                  md={6}
                  xs={12}
                  style={{ textAlign: 'center', marginTop: md ? theme.spacing(0) : theme.spacing(4), marginBottom: theme.spacing(4) }}
                >
                  <Typography
                    gutterBottom
                    fontFamily={theme.gh_vars.circular}
                    style={{ marginTop: theme.spacing(1.5), fontSize: 16, fontWeight: 700, display: 'inline-block', marginRight: theme.spacing(1.75) }}
                  >
                    Give us a call
                  </Typography>
                  <Typography style={{ display: 'inline-block' }}>
                    <a
                      href={`tel:1-${GENERAL_PHONE_NUMBER}`}
                      style={{ color: '#83A5B0' }}
                    >
                      {GENERAL_PHONE_NUMBER}
                    </a>
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={1365}
        margin={[0, 0]}
        style={{ marginTop: theme.spacing(11) }}
      >
        <Typography
          gutterBottom
          variant="h2"
          style={{ textAlign: 'center', fontSize: md ? 36 : 24, marginBottom: md ? theme.spacing(7.5) : theme.spacing(4) }}
        >
          Let us do the heavy lifting
        </Typography>

        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Box textAlign="center">
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  width: '100%',
                  height: md ? 360 : 256,
                  borderRadius: theme.shape.borderRadius
                }}
              >
                <Image
                  fill
                  src="/images/moving/packing.jpg"
                  alt=""
                  sizes="100vw"
                  style={{
                    objectFit: 'cover'
                  }}
                />
              </div>

              <Typography
                variant="h3Alt"
                style={{ fontSize: 18, marginTop: theme.spacing(3), marginBottom: theme.spacing(3) }}
              >
                Packing, moving & more 
              </Typography>

              <Typography className={classes.gridCopy}>
                From managing big moves to organizing clutter, we&apos;ve got you covered. Our transparent pricing includes labor, transportation and full insurance coverage.
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Box textAlign="center">
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  width: '100%',
                  height: md ? 360 : 256,
                  borderRadius: theme.shape.borderRadius,
                  backgroundColor: '#fff',
                  boxShadow: `0px 0px 10px ${rgba(theme.palette.tertiary.main, .5)}`,
                }}
              >
                <Image
                  fill
                  src="/images/moving/ui.png"
                  alt=""
                  sizes="100vw"
                  style={{
                    objectFit: 'cover',
                    padding: theme.spacing(2)
                  }}
                />
              </div>

              <Typography
                variant="h3Alt"
                style={{ fontSize: 18, marginTop: theme.spacing(3), marginBottom: theme.spacing(3) }}
              >
                Scheduled & completed in as little as 1 day
              </Typography>

              <Typography className={classes.gridCopy}>
                We handle all of the work for you, ensuring every move is completed professionally and on time. 
              </Typography>
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
          >
            <Box textAlign="center">
              <div
                style={{
                  overflow: 'hidden',
                  position: 'relative',
                  width: '100%',
                  height: md ? 360 : 256,
                  borderRadius: theme.shape.borderRadius
                }}
              >
                <Image
                  fill
                  src="/images/home-staging/professional-designers.png"
                  alt=""
                  sizes="100vw"
                  style={{
                    objectFit: 'cover'
                  }}
                />
              </div>

              <Typography
                variant="h3Alt"
                style={{ fontSize: 18, marginTop: theme.spacing(3), marginBottom: theme.spacing(3) }}
              >
                Support every step of the way 
              </Typography>

              <Typography className={classes.gridCopy}>
                Book online or talk with someone from our team. If you have questions or need to change dates, you can contact us anytime.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        margin={[4, 0]}
      >
        <Grid
          container
          justifyContent="center"
        >
          <Grid
            item
            xs={12}
            style={{ textAlign: 'center' }}
          >
            <Button
              size="large"
              variant="contained"
              color="secondary"
              component={ButtonLink}
              href="/home-staging/get-a-quote?step=email"
              style={{ width: md ? 420 : '100%', marginBottom: theme.spacing(3) }}
            >
              Get an instant quote
            </Button>
          </Grid>
          <Grid
            item
            xs={12}
            style={{ textAlign: 'center' }}
          >
            <Typography
              gutterBottom
              fontFamily={theme.gh_vars.circular}
              style={{ marginTop: theme.spacing(1.5), fontSize: 16, display: 'inline-block', marginRight: theme.spacing(1.75) }}
            >
              Give us a call
            </Typography>
            <Typography style={{ display: 'inline-block' }}>
              <a
                href={`tel:1-${GENERAL_PHONE_NUMBER}`}
                style={{ color: '#83A5B0' }}
              >
                {GENERAL_PHONE_NUMBER}
              </a>
            </Typography>
          </Grid>
        </Grid>
      </ContentBlock>

      <ContentBlock
        maxWidth={1365}
        margin={[12, 0]}
      >
        <Typography
          variant="h3Alt"
          style={{ fontSize: md ? 36 : 24, textAlign: 'center', marginBottom: md ? theme.spacing(7.5) : theme.spacing(4.5) }}
        >
          Services we offer:
        </Typography>

        <MovingIncludes />
      </ContentBlock>

      <ContentBlock
        maxWidth={1365}
        margin={[12, 0]}
      >
        <ContentBlock
          style={{
            backgroundColor: theme.palette.common.white,
            borderRadius: theme.shape.borderRadius,
            padding: md ? theme.spacing(4, 8, 8) : theme.spacing(2, 2, 6, 2)
          }}
        >
          <HowItWorksMovingContainer />
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={1365}
      >
        <Typography
          variant="h4Alt"
          style={{ fontSize: 24, textAlign: 'center', marginBottom: md ? theme.spacing(5) : theme.spacing(3) }}
        >
          What customers are saying
        </Typography>
        <ContentBlock
          maxWidth={1365}
          margin={[0, 'auto']}
          style={{ overflowX: 'hidden' }}
        >
          <Testimonials />
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        style={{ backgroundColor: '#f6f6f7', overflowX: 'hidden' }}
        padding={[12, 0]}
        margin={0}
      >
        <ContentBlock
          maxWidth={1365}
          margin={[0, 'auto']}
        >
          <Grid
            container
            style={{ marginBottom: theme.spacing(8) }}
          >
            <Grid
              item
              xs={12}
              md={7}
            >
              <Typography
                component="h2"
                variant="display4Alt"
              >
                See recent listings
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
            >
              <Typography
                gutterBottom
                style={{ fontSize: md ? 20 : 16, lineHeight: 1.5, marginTop: theme.spacing(2), marginBottom: theme.spacing(4) }}
              >
                Check out our work in Colorado, California & beyond 😎
              </Typography>
            </Grid>
          </Grid>

          {!!homes?.length && (
          <HomesCarousel
            seeAll
            homes={homes}
          />
          )}
        </ContentBlock>
      </ContentBlock>

    </>
  );
}

export default Moving;
